import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, Flag } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ReportChatDialogProps {
  jobId: string;
  reportedUserId?: string; // Made optional since backend can auto-determine it
  reportedUserName: string;
  isOpen: boolean;
  onClose: () => void;
}

const CHAT_REPORT_REASONS = [
  { value: "harassment", label: "Harassment or Bullying" },
  { value: "inappropriate_language", label: "Inappropriate Language" },
  { value: "spam", label: "Spam or Unwanted Content" },
  { value: "threats", label: "Threats or Violence" },
  { value: "scam_attempt", label: "Scam or Fraud Attempt" },
  { value: "unprofessional_conduct", label: "Unprofessional Conduct" },
  { value: "contact_sharing", label: "Attempting to Share Contact Information" },
  { value: "off_platform_solicitation", label: "Off-Platform Solicitation" },
  { value: "other", label: "Other" }
];

export const ReportChatDialog = ({ 
  jobId, 
  reportedUserId, 
  reportedUserName, 
  isOpen, 
  onClose 
}: ReportChatDialogProps) => {
  const [reportReason, setReportReason] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!reportReason) {
      toast({
        title: "Please select a reason",
        description: "You must select a reason for reporting this conversation.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Submitting chat report:", {
        jobId,
        reportedUserId,
        reportReason,
        description
      });

      // Submit chat report via edge function
      const { error } = await supabase.functions.invoke('submit-chat-report', {
        body: {
          job_id: jobId,
          ...(reportedUserId && { reported_user_id: reportedUserId }), // Only include if provided
          report_reason: reportReason,
          report_description: description || null
        }
      });

      if (error) throw error;

      toast({
        title: "Chat reported successfully",
        description: "Thank you for reporting this conversation. Our administrators will review it promptly and take appropriate action.",
      });

      // Reset form and close
      setReportReason("");
      setDescription("");
      onClose();
    } catch (error: any) {
      console.error("Error submitting chat report:", error);
      toast({
        title: "Failed to submit report",
        description: error.message || "An error occurred while submitting your report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md z-[60]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-red-500" />
            Report Conversation
          </DialogTitle>
          <DialogDescription>
            Report this entire conversation with <strong>{reportedUserName}</strong> to administrators for review.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Important</p>
                <p>This will report the entire conversation and all messages exchanged. False reports may result in restrictions on your account.</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for reporting this conversation *</Label>
            <Select value={reportReason} onValueChange={setReportReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select a reason..." />
              </SelectTrigger>
              <SelectContent className="z-[70]">
                {CHAT_REPORT_REASONS.map((reason) => (
                  <SelectItem key={reason.value} value={reason.value}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Additional details (optional)</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide any additional context about why you're reporting this entire conversation..."
              rows={4}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground">
              {description.length}/1000 characters
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="text-sm text-blue-800">
              <p className="font-medium">What happens next?</p>
              <ul className="mt-2 space-y-1 text-sm">
                <li>• Our team will review the entire conversation</li>
                <li>• We may take action including warnings or account restrictions</li>
                <li>• You'll be notified of the outcome via email</li>
                <li>• All reports are handled confidentially</li>
              </ul>
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} className="flex-1">
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
              ) : null}
              Report Conversation
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};